/**
 * Angular core import.
 */
import { Component, OnInit, OnDestroy, ViewChild, Input, ElementRef } from '@angular/core';
/**
 * rxjs import.
 */
import { Subject, Subscription, timer } from 'rxjs';
import { takeUntil, takeWhile, tap } from 'rxjs/operators';

/**
 * Services import.
 */
import { NgxCaptureService } from 'ngx-capture';
import { NCP } from '../model/ncp.model';
import { NewLanguageService } from '../../dataservices/new-language/new-language.service';
import { DataStorageService } from '../../dataservices/data-storage.service';
import { FunCollectionService } from '../../common/common-functions/fun-collection.service';
import { MasterserviceService } from './../../dataservices/master-service/masterservice.service';
import { ImageGroupService } from '../../dataservices/master-service/image-group.service';

/**
 * Import interface.
 */
import { AnalyticsInterface } from '../../analytics-event/analytics.model';
/**
* Import constant.
*/
import { NUMBERS, PRODUCT_TYPE_CONSTANT, SCREEN_NAME } from '../../constants/app.constants';
import { EVENT_NAME } from '../../analytics-event/analytics.constant';

@Component({
  selector: 'app-healthcard',
  templateUrl: './healthcard.component.html',
  styleUrls: ['./healthcard.component.scss']
})
export class HealthcardComponent implements OnInit, OnDestroy {
  /**
   * Input used to set data in the card.
   */
  @Input() product: NCP;

  /**
   * Time remaining to expire the card.
   */
  public remainingDuration: string;
  /**
   * key used to hide the timer.
   */
  public hideTimer = false;
  /**
   * Variable to store time duration.
   */
  private duration: number;
  /**
   * Notifier of multiple actions.
   */
  private notifier$ = new Subject();
  /**
   * ViewChild used to get a block of HTML code.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @ViewChild('screen', { static: true }) screen: ElementRef<any>;
  /**
   * Image name while downloading the image.
   */
  public imageTitle = 'HealthCard';
  /**
   * Key used to store share image encoded url.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public shareImage: any;
  public loading = false;
  public downLoading = false;

  /**
   * Analytics share and download var.
   */
  public analyticsShareData: AnalyticsInterface;
  public analyticsDownloadData: AnalyticsInterface;

  constructor(
    private captureService: NgxCaptureService,
    public mls: NewLanguageService,
    public dss: DataStorageService,
    private masterService: MasterserviceService,
    private funCollectionService: FunCollectionService,
    public igs: ImageGroupService) { }

  ngOnInit(): void {
    this.remainingDuration = '';
    this.duration = (this.product && this.product.products && this.product.products.length && (this.product.products[0].expiry_time)) - (new Date().getTime() / 1000);
    // this.duration = (new Date().getTime() + 20) - new Date().getTime();
    this.duration = Math.floor(this.duration);
    if (this.duration <= NUMBERS.ONE) {
      this.hideTimer = true;
    } else {
      this.startTimer();
    }



    /**
     * Analytics objects.
     */
    this.analyticsShareData = {
      'event_name': EVENT_NAME.HEALTH_CARD_SHARE_CLICK,
      'product_type': PRODUCT_TYPE_CONSTANT.NCP_SUB_TYPE,
      'product_type_id': this.product.products[0].ncp_product[0].product_type_id,
      'screen_name': SCREEN_NAME.GRAND_ROUND,
    };

    this.analyticsDownloadData = {
      'event_name': EVENT_NAME.HEALTH_CARD_DOWNLOAD_CLICK,
      'product_type': PRODUCT_TYPE_CONSTANT.NCP_SUB_TYPE,
      'product_type_id': this.product.products[0].ncp_product[0].product_type_id,
      'screen_name': SCREEN_NAME.GRAND_ROUND,
    };
  }

  /**
   * start count down timer.
   */
  startTimer(): Subscription {
    return timer(0, 1000).pipe(
      tap(() => {
        return this.getTimeDifference(--this.duration);
      }),
      takeWhile(() => this.duration > -1),
      tap(() => {
        if (this.duration === NUMBERS.ZERO) {
          this.hideTimer = true;
        }
      })
    ).subscribe();
  }

  /**
   * Download health card.
   */
  download(): void {
    this.downLoading = true;
    const dimensions = this.screen.nativeElement.getBoundingClientRect();
    this.captureService
      .getImage(this.screen.nativeElement, false, {
        width: dimensions.width,
        height: dimensions.height,
        useCORS: true,
      })
      .pipe(
        tap((img) => {

          this.funCollectionService.downloadImage(img, this.imageTitle);
          this.downLoading = false;
        }),
        takeUntil(this.notifier$)
      )
      .subscribe();
  }

  /**
   * Time difference.
   */
  getTimeDifference(duration: number): void {
    this.remainingDuration = '';
    const hour = Math.floor(duration / 3600);
    const min = Math.floor(duration % 3600 / 60);
    const sec = Math.floor(duration % 3600 % 60);
    if (hour > 0) {
      this.remainingDuration = `${('00' + hour).slice(-2)}h : `;
    }
    this.remainingDuration += `${('00' + (min < 0 ? '00' : min)).slice(-2)}m : ${('00' + (sec < 0 ? '00' : sec)).slice(-2)}s`;
  }

  /**
   * Share Product
   */
  shareProduct(): boolean {
    this.loading = true;
    const dimensions = this.screen.nativeElement.getBoundingClientRect();
    this.captureService.getImage(this.screen.nativeElement, false, {
      width: dimensions.width,
      height: dimensions.height,
      useCORS: true,
    }).pipe(
      tap(imgUrl => {
        this.shareImage = imgUrl;
        this.funCollectionService.updatedSharedHealthCard(imgUrl);
        const share = [{
          share_url: '',
          share_content: '',
          product_type: this.product.products[0].product_type,
          product_type_id: this.product.products[0].product_type_id
        }];
        this.funCollectionService.productShare(share);
        this.loading = false;
      }),
      takeUntil(this.notifier$)
    ).subscribe();

    if (this.masterService.isOnline == false) {
      this.masterService.isConnectionModalShown.next('1');
      return false;
    }
  }

  /**
   * Unsubscribe and finish process.
   */
  ngOnDestroy(): void {
    this.notifier$.next();
    this.notifier$.complete();
  }
}
